"use client";

import React from "react";
import { motion } from "framer-motion";
import { CareersBenefits } from "src/utils/mock/CareersData";
import { TrendingUp, Clock3, GraduationCap, Trophy, BriefcaseBusiness, HeartHandshake } from "lucide-react";

const iconStyles = [
  {
    bg: "bg-purple-500/10",
    border: "border-purple-500/20",
    icon: "text-purple-600 dark:text-purple-400",
    glow: "0 0 40px rgba(168,85,247,0.18)",
  },
  {
    bg: "bg-blue-500/10",
    border: "border-blue-500/20",
    icon: "text-blue-600 dark:text-blue-400",
    glow: "0 0 40px rgba(59,130,246,0.18)",
  },
  {
    bg: "bg-green-500/10",
    border: "border-green-500/20",
    icon: "text-green-600 dark:text-green-400",
    glow: "0 0 40px rgba(34,197,94,0.18)",
  },
  {
    bg: "bg-pink-500/10",
    border: "border-pink-500/20",
    icon: "text-pink-600 dark:text-pink-400",
    glow: "0 0 40px rgba(236,72,153,0.18)",
  },
  {
    bg: "bg-violet-500/10",
    border: "border-violet-500/20",
    icon: "text-violet-600 dark:text-violet-400",
    glow: "0 0 40px rgba(139,92,246,0.18)",
  },
  {
    bg: "bg-yellow-500/10",
    border: "border-yellow-500/20",
    icon: "text-yellow-600 dark:text-yellow-400",
    glow: "0 0 40px rgba(234,179,8,0.18)",
  },
];

const CardInner = ({ benefit, index, Icon }) => {
  const style = iconStyles[index % iconStyles.length];

  return (
    <div className="relative z-10 h-full w-full">
      <div className="flex flex-col sm:flex-row items-start gap-4 w-full">
        <div
          className={`self-center sm:self-auto flex-shrink-0 w-[72px] h-[72px] rounded-full flex items-center justify-center border ${style.bg} ${style.border}`}
          style={{
            boxShadow: style.glow,
          }}
        >
          <Icon className={`${style.icon} h-7 w-7`} strokeWidth={1.8} />
        </div>

        <div className="flex-1 min-w-0 overflow-hidden">
          <span className="block text-[var(--c-ink-3)] text-[24px] leading-none font-semibold tracking-[-0.04em]">{String(index + 1).padStart(2, "0")}</span>

          <h3 className="!mt-3 text-[21px] leading-[1.25] font-semibold text-text-primary tracking-[-0.03em] break-words overflow-hidden">{benefit.heading}</h3>

          <p className="!mt-3 text-[14px] leading-[1.75] text-text-secondary break-words overflow-hidden">{benefit.content}</p>
        </div>
      </div>
    </div>
  );
};

const BenefitCard = ({ benefit, index, Icon }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{
      duration: 0.6,
      delay: index * 0.06,
      ease: [0.22, 1, 0.36, 1],
    }}
    whileHover={{
      y: -5,
      borderColor: "var(--border-secondary)",
    }}
    className="relative rounded-md border border-border-primary dark:border-white/[0.06] overflow-hidden backdrop-blur-xl transition-all duration-300 w-full"
    style={{
      background: "var(--card-background)",
      boxShadow: "var(--card-shadow)",
    }}
  >
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.08),transparent_40%)]" />

    <div className="absolute inset-0 rounded-[30px] border border-border-primary/10" />

    <div className="relative !p-7 min-h-[250px] w-full overflow-hidden">
      <CardInner benefit={benefit} index={index} Icon={Icon} />
    </div>
  </motion.div>
);

const BuildCareerSection = () => {
  const icons = [GraduationCap, Clock3, TrendingUp, HeartHandshake, BriefcaseBusiness, Trophy];

  const items = CareersBenefits.map((item, index) => ({
    ...item,
    Icon: icons[index % icons.length],
    index,
  })).filter((item) => item.heading);

  return (
    <section id="career-benefits" className="relative pt-10! md:pt-12! !px-6 overflow-hidden">
      <div className="relative w-full max-w-[2200px] mx-auto grid grid-cols-1 lg:grid-cols-[0.7fr_2.3fr] gap-10 items-start lg:pl-16 xl:pl-24">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:sticky lg:top-28 !pt-6 lg:pl-8! lg:-translate-x-0"
        >
          <div className="flex items-center gap-4 mb-4!">
            <h3 className="text-xl sm:text-2xl font-medium uppercase text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600">
              {" "}
              Benefits
            </h3>
            <span className="w-16 h-[2px] bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 rounded-full"></span>
          </div>

          <h2 className="!mt-2 text-text-primary leading-[1.05] tracking-[-0.05em] lg:text-[40px] text-[30px]">Why Join Tycho</h2>

          <p className="!mt-3 text-text-secondary  text-[15px] leading-[1.7] max-w-[280px]">A culture built on trust, growth, and genuine human connection — from day one.</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 w-full ">
          {items.map((item) => (
            <BenefitCard key={item.heading} benefit={item} index={item.index} Icon={item.Icon} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default BuildCareerSection;
